import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { IPaymentNotificationsUI } from './types'
import { PaymentNotificationsUI } from './ui'
import { usePaymentNotifications } from '@api/paymentNotifications'

import { useValue } from '@hooks/useValue'
import { useDebounce } from '@hooks/useDebounce'

import { CardContractContainer, ICardContractContainer } from '@smart/cardContract'
import { CardListenerContainer, ICardListenerContainer } from '@smart/cardListener'
import { CardApplicationContainer, ICardApplicationContainer } from '@smart/cardApplication'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


/** Контейнер страницы - Извещения о платежах */
export const PaymentNotificationsContainer = () => {
    const notify = useContext(NotificationContext)

    const currentPageNumber = useValue<number>(1)
    const txtSearch = useValue<string>('')
    const cardContract = useValue<ICardContractContainer>({ isOpen: false })
    const cardListener = useValue<ICardListenerContainer>({ isOpen: false })
    const cardApplication = useValue<ICardApplicationContainer>({ isOpen: false })

    const paymentNotificationsState = usePaymentNotifications(useShallow((state) => state))

    useEffect(() => {
        return (() => {
            paymentNotificationsState.clearPaymentNotificationsState()
            currentPageNumber.setValue(1)
            txtSearch.setValue('')
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (paymentNotificationsState.error) return notify && notifyOpen(paymentNotificationsState.error.message, paymentNotificationsState.error.type, 3000, notify)
    }, [paymentNotificationsState.error]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadPrimaryData(txtSearch.value)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    /** Получение списка извещений о платежах */
    const loadPrimaryData = (search?: string) => {
        paymentNotificationsState.actionGetPaymentNotificationsList(currentPageNumber.value, search || '')
    }

    /** Открытие/закрытие анкеты договора */
    const handleOpenCardContract = (isOpen: boolean, contractID?: number) => {
        cardContract.setValue({
            isOpen, cardMode: 'edit', cardID: contractID,
            handlerButtonCancel() { cardContract.setValue({ isOpen: false }) },
            handlerButtonOk() {
                cardContract.setValue({ isOpen: false })
                loadPrimaryData(txtSearch.value)
            },
        })
    }

    /** Открытие/закрытие анкеты слушателя */
    const handleOpenCardListener = (isOpen: boolean, listenerID?: number) => {
        cardListener.setValue({
            isOpen, cardMode: 'edit', cardID: listenerID,
            handlerButtonCancel() { cardListener.setValue({ isOpen: false }) },
            handlerButtonOk() {
                cardListener.setValue({ isOpen: false })
                loadPrimaryData(txtSearch.value)
            },
        })
    }

    /** Открытие/закрытие анкеты заявки */
    const handleOpenCardApplication = (isOpen: boolean, applicationID?: number) => {
        cardApplication.setValue({
            isOpen, cardMode: 'edit', cardID: applicationID,
            handleButtonCancel() { cardApplication.setValue({ isOpen: false }) },
            handleButtonOk() {
                cardApplication.setValue({ isOpen: false })
                loadPrimaryData(txtSearch.value)
            },
        })
    }

    const debounceSearch = useDebounce(loadPrimaryData, 500) // Запуск поиска через полсекунды
    const txtSearchOnChange = (value: string) => { txtSearch.setValue(value); debounceSearch(value) } // Поле поиска

    /** Свойства - UI Извещений о платежах */
    const propsUI: IPaymentNotificationsUI = {
        isLoading: paymentNotificationsState.notifications.isLoading,
        txtSearch,
        currentPageNumber,
        totalCount: paymentNotificationsState.notifications.totalCount,
        pageSize: paymentNotificationsState.notifications.pageSize,
        notificationsList: paymentNotificationsState.notifications.items,
        cardContract,
        cardListener,
        txtSearchOnChange,
        loadPrimaryData,
        handleOpenCardContract,
        handleOpenCardListener,
        handleOpenCardApplication,
    }

    return <>
        <PaymentNotificationsUI {...propsUI} />
        <CardContractContainer {...cardContract.value} />
        <CardListenerContainer {...cardListener.value} />
        <CardApplicationContainer {...cardApplication.value} />
    </>
}